import React from "react";
import { FormFieldLabel } from "../../../atoms/proc-management/form";
import { InputNoticeList } from "./input-notice-list";
import type { InputProps, PathProps } from "./types";
import { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import apiClient from "../../../../application/apis/api-client";
import {
  isNotFoundError,
  isPermissionError,
  reportProcManagementRelatedError,
} from "../../../../application/domain/proc-management/util/error";
import { PublishStatus } from "../../../../application/domain/procedures/procedure-entity";
import { ProcedureStatusTag } from "../../../molecules/procedure-status-tag";
import { ServiceProviderR } from "../../../pages/service-provider/routes";
import { SmartApplyR } from "../../../pages/smart-apply/route";
import { ProcedureOnlineInfoSmartApplyNotification } from "./procedure-online-info-smart-apply-notification";
import type {
  SmartApplyValidationResult,
  StatusValue,
} from "../../../../application/domain/proc-management";

type Props = InputProps &
  PathProps & {
    value?: string;
    linkForSmartApplyProcs: string;
    isServiceProviderAccount: boolean;
    orgId: string;
    errorMsg?: string;
    currentStatus: StatusValue;
    smartApplyValidationResult: SmartApplyValidationResult;
  };

type OptionType = {
  label: string;
  value: string;
  status: PublishStatus;
};

export const ProcedureOnlineInfoSmartApplyIdInput: React.FC<Props> = ({
  onChange,
  disabled = false,
  basePath,
  value,
  linkForSmartApplyProcs,
  isServiceProviderAccount = false,
  orgId, // FIXME: used only for service provider account
  shouldDisplayError = false,
  errorMsg,
  currentStatus,
  smartApplyValidationResult,
}) => {
  const linkForSmartApplyProcDetail = isServiceProviderAccount
    ? ServiceProviderR.ProcedureDetail.link({
        orgId: orgId,
        procedureId: value ?? "",
      })
    : SmartApplyR.ProcedureDetail.link({
        id: value ?? "",
      });
  const notices = [
    <a href={linkForSmartApplyProcs} target="_blank" rel="noopener noreferrer">
      スマート申請一覧
    </a>,
    "ユーザーがアクセスするURLは、保存時に自動で補完および更新されます。",
  ];
  const [name, setName] = useState<string>("");
  const [bouncedName] = useDebounce(name, 500);
  const [currentSmartApply, setCurrentSmartApply] = useState<OptionType>();
  const [options, setOptions] = useState<OptionType[]>([]);
  const [isDisplayId, setIsDisplayId] = useState(false);

  useEffect(() => {
    const fetchCurrentSmartApply = async () => {
      if (value === undefined || value === "") {
        return;
      }
      try {
        const { data: smartApply } = await apiClient.fetchProcedure(value);
        setCurrentSmartApply({
          label: smartApply.name,
          value: smartApply.id,
          status: smartApply.publishStatus,
        });
      } catch (e) {
        reportProcManagementRelatedError(e);
        if (isNotFoundError(e)) {
          toast.error(
            `スマート申請を見つけることができませんでした。id: ${value}`,
          );
        } else if (isPermissionError(e)) {
          toast.error(`スマート申請を表示する権限がありません。id: ${value}`);
        } else {
          toast.error(
            `スマート申請の取得に失敗しました。電波状況のよい場所でリロードしてください。`,
          );
        }
      }
    };
    fetchCurrentSmartApply();
  }, [value, setCurrentSmartApply]);

  useEffect(() => {
    const fetchCandidateSmartApplies = async () => {
      // 手続きを入力して絞り込むことを想定しているので50件で決め打ちしている
      // ref. https://graffer.slack.com/archives/CRHQZCMDF/p1664445197448889?thread_ts=1664443287.704369&cid=CRHQZCMDF
      const candidateLimit = 50;

      try {
        const { data: smartApplies } = isServiceProviderAccount
          ? await apiClient.fetchProceduresByOrgId(orgId, 1, candidateLimit, {
              name: bouncedName,
            })
          : await apiClient.fetchProcedures(1, candidateLimit, {
              name: bouncedName,
            });
        const candidateSmartApplies: OptionType[] = [];
        smartApplies.forEach((s) =>
          candidateSmartApplies.push({
            label: s.name,
            value: s.id,
            status: s.publishStatus,
          }),
        );
        setOptions(candidateSmartApplies);
      } catch (e) {
        reportProcManagementRelatedError(e);
        if (isNotFoundError(e)) {
          toast.error(`スマート申請一覧の取得に失敗しました。`);
        } else if (isPermissionError(e)) {
          toast.error(`スマート申請を表示する権限がありません。`);
        } else {
          toast.error(
            `スマート申請一覧の取得に失敗しました。電波状況のよい場所でリロードしてください。`,
          );
        }
      }
    };
    fetchCandidateSmartApplies();
  }, [bouncedName, orgId, isServiceProviderAccount, setOptions]);

  const formatOptionLabel = (option: OptionType) => (
    <div>
      <span className="mr-4">{option.label}</span>
      {isDisplayId && (
        <span className="has-text-grey-light mr-4">{option.value}</span>
      )}
      <ProcedureStatusTag status={option.status} />
    </div>
  );
  const handleClick = React.useCallback(() => {
    setIsDisplayId(!isDisplayId);
  }, [isDisplayId, setIsDisplayId]);

  const preferredErrorMessage =
    typeof smartApplyValidationResult.error === "string" &&
    smartApplyValidationResult.error.length > 0
      ? smartApplyValidationResult.error
      : typeof errorMsg === "string" && errorMsg.length > 0
        ? errorMsg
        : undefined;
  const isDisplayingError =
    typeof smartApplyValidationResult.error === "string" ||
    (typeof preferredErrorMessage === "string" && shouldDisplayError);
  const additionalInputStyle = isDisplayingError
    ? { borderColor: "#f14668" } // same style as "is-danger input" in bulma
    : {};
  return (
    <div className="field mb-6">
      <FormFieldLabel label="スマート申請" required />
      <InputNoticeList notices={notices} />
      <Select
        styles={{
          control: (base) => ({
            ...base,
            ...additionalInputStyle,
          }),
        }}
        placeholder={"選択してください"}
        value={currentSmartApply}
        onInputChange={(input) => {
          setName(input);
        }}
        onChange={(opt) => {
          if (opt && opt.value) {
            onChange([...basePath], opt.value);
          }
        }}
        options={options}
        formatOptionLabel={formatOptionLabel}
        isDisabled={disabled}
      />
      <label className="checkbox is-size-7 mt-2">
        <input
          type="checkbox"
          className="mx-1"
          checked={isDisplayId}
          onClick={handleClick}
        />
        IDを表示
      </label>
      {isDisplayingError && (
        <p className="help has-text-weight-normal is-danger">
          {preferredErrorMessage}
        </p>
      )}
      {currentSmartApply && (
        <ProcedureOnlineInfoSmartApplyNotification
          linkForSmartApplyProcDetail={linkForSmartApplyProcDetail}
          procStatus={currentStatus}
          smartApplyStatus={currentSmartApply.status}
        />
      )}
    </div>
  );
};
